import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dna, Heart, Target, TrendingUp, Shield, Zap, Users, Settings2,
  Plus, X, Save, RefreshCw, Brain, Sparkles, Lock, Unlock
} from 'lucide-react';
import {
  getGenomeHealth, initGenome, updateGenomeProducts,
  toggleGenomeCollective, getGenomeContext
} from '../lib/api';

const INDUSTRIES = [
  { value: 'oswietlenie', label: 'Oswietlenie', emoji: '💡' },
  { value: 'meble', label: 'Meble', emoji: '🪑' },
  { value: 'budownictwo', label: 'Budownictwo', emoji: '🏗️' },
  { value: 'elektronika', label: 'Elektronika', emoji: '🔌' },
  { value: 'zywnosc', label: 'Zywnosc', emoji: '🍎' },
  { value: 'chemia', label: 'Chemia', emoji: '🧪' },
  { value: 'rolnictwo', label: 'Rolnictwo', emoji: '🌾' },
  { value: 'tekstylia', label: 'Tekstylia', emoji: '🧵' },
  { value: 'czesci_samochodowe', label: 'Czesci samochodowe', emoji: '🚗' },
  { value: 'custom', label: 'Inna branza', emoji: '⚙️' },
];

const MARKETS = [
  { value: 'b2b_lokalny', label: 'B2B Lokalny' },
  { value: 'b2b_krajowy', label: 'B2B Krajowy' },
  { value: 'b2b_eksport', label: 'B2B Eksport' },
  { value: 'b2c', label: 'B2C' },
  { value: 'mieszany', label: 'Mieszany' },
];

export default function GenomeDashboard({ lang, tenantId }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInit, setShowInit] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [contextData, setContextData] = useState(null);

  // Init form
  const [initIndustry, setInitIndustry] = useState('custom');
  const [initCustom, setInitCustom] = useState('');
  const [initMarket, setInitMarket] = useState('b2b_krajowy');
  const [initGeo, setInitGeo] = useState('PL');

  // Products form
  const [usp, setUsp] = useState('');
  const [redLines, setRedLines] = useState('');
  const [bestsellers, setBestsellers] = useState('');
  const [deadStock, setDeadStock] = useState('');
  const [marginFloors, setMarginFloors] = useState('');

  const loadHealth = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getGenomeHealth(tenantId);
      setHealth(data);
      if (data.status === 'no_genome') {
        setShowInit(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  async function handleInit() {
    try {
      await initGenome({
        tenant_id: tenantId,
        industry: initIndustry,
        industry_custom: initCustom || undefined,
        market: initMarket,
        geo_focus: initGeo,
      });
      toast.success('Genome zainicjalizowany!');
      setShowInit(false);
      loadHealth();
    } catch (err) {
      toast.error('Blad inicjalizacji: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handleUpdateProducts() {
    try {
      const data = {};
      if (usp) data.usp = usp.split('\n').filter(Boolean);
      if (redLines) data.red_lines = redLines.split('\n').filter(Boolean);
      if (bestsellers) data.bestsellers = bestsellers.split('\n').filter(Boolean);
      if (deadStock) data.dead_stock = deadStock.split('\n').filter(Boolean);
      if (marginFloors) {
        try {
          data.margin_floors = JSON.parse(marginFloors);
        } catch { /* skip */ }
      }
      await updateGenomeProducts(data, tenantId);
      toast.success('DNA produktowe zaktualizowane!');
      setShowProducts(false);
      loadHealth();
    } catch (err) {
      toast.error('Blad: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handleToggleCollective() {
    if (!health || health.status === 'no_genome') return;
    const currentOpt = health.collective?.opt_in || false;
    try {
      await toggleGenomeCollective(tenantId, !currentOpt);
      toast.success(!currentOpt ? 'Dolaczono do Collective Pool!' : 'Wypisano z Collective Pool');
      loadHealth();
    } catch (err) {
      toast.error('Blad: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handlePreviewContext() {
    try {
      const ctx = await getGenomeContext(tenantId, 'unknown');
      setContextData(ctx);
      setShowContext(true);
    } catch (err) {
      toast.error('Blad: ' + (err.response?.data?.detail || err.message));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Dna className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No genome — show init wizard
  if (!health || health.status === 'no_genome' || showInit) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Dna className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Domain Genome</h1>
          <p className="text-muted-foreground mt-2">
            Stwórz żywy model wiedzy Twojej firmy. Genome uczy się z każdej negocjacji.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Inicjalizacja Genome
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Branza</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind.value}
                    onClick={() => setInitIndustry(ind.value)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      initIndustry === ind.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <span className="text-lg mr-2">{ind.emoji}</span>
                    <span className="text-sm font-medium">{ind.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {initIndustry === 'custom' && (
              <div>
                <label className="text-sm font-medium mb-1 block">Nazwa branzy</label>
                <Input
                  value={initCustom}
                  onChange={e => setInitCustom(e.target.value)}
                  placeholder="np. Odziez sportowa, Logistyka..."
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Rynek</label>
              <div className="flex flex-wrap gap-2">
                {MARKETS.map(m => (
                  <button
                    key={m.value}
                    onClick={() => setInitMarket(m.value)}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                      initMarket === m.value
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Rynki geograficzne</label>
              <Input
                value={initGeo}
                onChange={e => setInitGeo(e.target.value)}
                placeholder="PL,DE,CZ (rozdzielone przecinkami)"
              />
            </div>

            <Button onClick={handleInit} className="w-full" size="lg">
              <Dna className="w-4 h-4 mr-2" />
              Stwórz Genome
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Genome exists — show dashboard
  const score = health.health_score || 0;
  const scoreColor = score >= 70 ? 'text-green-400' : score >= 40 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Dna className="w-7 h-7 text-primary" />
            Domain Genome
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            v{health.version} | {health.industry} | Ostatnia ewolucja: {health.last_evolved ? new Date(health.last_evolved).toLocaleDateString('pl') : 'brak'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePreviewContext}>
            <Brain className="w-4 h-4 mr-1" /> Podglad kontekstu
          </Button>
          <Button variant="outline" size="sm" onClick={loadHealth}>
            <RefreshCw className="w-4 h-4 mr-1" /> Odswież
          </Button>
        </div>
      </div>

      {/* Health Score */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="md:col-span-1">
          <CardContent className="pt-6 text-center">
            <div className={`text-5xl font-bold ${scoreColor}`}>{score}</div>
            <div className="text-sm text-muted-foreground mt-1">Health Score</div>
            <Progress value={score} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Heart className="w-4 h-4" /> Dojrzalosc
            </div>
            <div className="text-3xl font-bold">{health.maturity}%</div>
            <Progress value={health.maturity} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" /> Win Rate
            </div>
            <div className="text-3xl font-bold text-green-400">{health.stats?.win_rate || '0%'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {health.stats?.total_negotiations || 0} negocjacji
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Target className="w-4 h-4" /> Srednia marza
            </div>
            <div className="text-3xl font-bold text-blue-400">{health.stats?.avg_margin || '0%'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {health.stats?.tactics_learned || 0} taktyk | {health.stats?.archetypes_profiled || 0} archetypów
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Tactics + Collective */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="w-5 h-5 text-yellow-400" /> Top taktyki
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health.top_tactics?.length > 0 ? (
              <div className="space-y-3">
                {health.top_tactics.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                    <div>
                      <div className="font-medium text-sm">{t.name.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-muted-foreground">{t.uses} uzyc</div>
                    </div>
                    <Badge variant={parseFloat(t.win_rate) >= 60 ? 'default' : 'secondary'}>
                      {t.win_rate}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Brak danych — przeprowadz pierwsza negocjacje.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-5 h-5 text-purple-400" /> Collective Intelligence Pool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                <div>
                  <div className="flex items-center gap-2">
                    {health.collective?.opt_in ? (
                      <Unlock className="w-4 h-4 text-green-400" />
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm">
                      {health.collective?.opt_in ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Anonimowe dzielenie taktyk miedzy branzami.
                    {health.collective?.opt_in && ` Zniżka: ${health.collective.discount}`}
                  </p>
                </div>
                <Button
                  variant={health.collective?.opt_in ? 'outline' : 'default'}
                  size="sm"
                  onClick={handleToggleCollective}
                >
                  {health.collective?.opt_in ? 'Wypisz' : 'Dolacz'}
                </Button>
              </div>
              {health.collective?.opt_in && (
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <div className="text-2xl font-bold text-primary">{health.collective.shared}</div>
                    <div className="text-xs text-muted-foreground">Udostepnione</div>
                  </div>
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <div className="text-2xl font-bold text-purple-400">{health.collective.received}</div>
                    <div className="text-xs text-muted-foreground">Otrzymane</div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product DNA Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="w-5 h-5 text-primary" /> DNA Produktowe
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowProducts(!showProducts)}>
              {showProducts ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {showProducts ? 'Zamknij' : 'Edytuj'}
            </Button>
          </div>
        </CardHeader>
        {showProducts && (
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">USP (przewagi konkurencyjne)</label>
              <Textarea
                value={usp}
                onChange={e => setUsp(e.target.value)}
                placeholder="Jedna przewaga per linia, np:&#10;Szybka dostawa 24h&#10;Gwarancja 5 lat&#10;Darmowe doradztwo techniczne"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Red Lines (zasady ktorych MASKIL nigdy nie przekroczy)</label>
              <Textarea
                value={redLines}
                onChange={e => setRedLines(e.target.value)}
                placeholder="Jedna zasada per linia, np:&#10;Nigdy nie schodz ponizej marzy minimalnej&#10;Nie obiecuj terminow ktorych nie mozesz dotrzymac"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Bestsellery</label>
                <Textarea
                  value={bestsellers}
                  onChange={e => setBestsellers(e.target.value)}
                  placeholder="SKU lub nazwy (per linia)"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Dead Stock (do upłynnienia jako bonus)</label>
                <Textarea
                  value={deadStock}
                  onChange={e => setDeadStock(e.target.value)}
                  placeholder="Produkty do oddania gratis (per linia)"
                  rows={3}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Marze minimalne (JSON)</label>
              <Input
                value={marginFloors}
                onChange={e => setMarginFloors(e.target.value)}
                placeholder='{"premium": 0.35, "standard": 0.22, "economy": 0.10}'
              />
            </div>
            <Button onClick={handleUpdateProducts}>
              <Save className="w-4 h-4 mr-2" /> Zapisz DNA
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Context Preview Modal */}
      {showContext && contextData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Brain className="w-5 h-5 text-primary" /> Podglad kontekstu Genome
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowContext(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-black/50 p-4 rounded-lg text-xs text-green-400 overflow-auto max-h-96 font-mono">
              {JSON.stringify(contextData, null, 2)}
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              Ten JSON jest wstrzykiwany do Phase 2 (Strategist) przed kazda sesja negocjacyjna.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
